// Import react elements
import { useEffect, useMemo, useState } from "react";

// Import components
import CreateButton from "global/components/CreateButton";
import { ImageLoader } from "global/components/ImageLoader";
import TwoColumnList from "global/components/TwoColumnList";
import DropdownSelect from "global/components/DropdownSelect";
import { CloseIcon } from "global/strich_icons/StrichIcons";
import LoadingScreen from "global/components/loading_ui/LoadingScreen";



// Clone a saved element array so editing local view settings cannot mutate API data by reference
const cloneElements = (elements) => {

    if (!Array.isArray(elements)) { return []; }

    return JSON.parse(JSON.stringify(elements));

};


// Build a stable comparison snapshot containing only editable print-area view settings
const getPrintAreaViewSettingsSnapshot = (elements) => {

    if (!Array.isArray(elements)) { return "[]"; }

    const snapshot = [];

    // Capture each print area's physical identity and normalised view settings
    elements.forEach((element) => {

        if (element?.type !== "print_area") { return; }

        const aspects = Array.isArray(element?.data?.view_settings?.aspects)
            ? [...element.data.view_settings.aspects].map((aspect) => String(aspect).trim().toUpperCase()).sort()
            : [];

        const rotations = Array.isArray(element?.data?.view_settings?.rotations)
            ? [...element.data.view_settings.rotations].map((rotation) => Number(rotation)).sort((a, b) => a - b)
            : [];

        snapshot.push({
            product_code: String(element?.product_code || ""),
            surface_code: String(element?.surface_code || ""),
            print_area_code: String(element?.print_area_code || ""),
            aspects: aspects,
            rotations: rotations,
        });

    });

    return JSON.stringify(snapshot);

};


// Render MOCKUP pop up - show larger display image with per-print-area compatibility settings and delete ability
const RenderMockupPopup = ({ isOpen, onClose, mockup, productCode, customProduct, fetchMockup, getMockupCloneTargets, handleCloneMockup, handleUpdateMockup, handleDeleteMockup, deleteLoading, handleOpenCustomizrClick, isCustomizrReady }) => {

    // Store the complete editable element set so existing image ids and placement data are preserved on save
    const [mockupElements, setMockupElements] = useState([]);
    const [savedMockupElements, setSavedMockupElements] = useState([]);
    const [loadingMockupDetails, setLoadingMockupDetails] = useState(false);
    const [mockupLoadError, setMockupLoadError] = useState("");
    const [saving, setSaving] = useState(false);

    // Store the available clone destinations for this product family
    const [cloneTargets, setCloneTargets] = useState([]);
    const [selectedCloneTarget, setSelectedCloneTarget] = useState("");
    const [cloning, setCloning] = useState(false);


    // Load the complete saved element set, including image ids, whenever this popup opens for a mockup
    useEffect(() => {

        let isCurrent = true;

        // Reset the editable element state while the selected mockup changes
        setMockupElements([]);
        setSavedMockupElements([]);
        setMockupLoadError("");

        if (!isOpen || !mockup?.id || !fetchMockup) { return () => { isCurrent = false; }; }

        const loadMockup = async () => {

            setLoadingMockupDetails(true);

            const response = await fetchMockup(mockup.id);

            if (!isCurrent) { return; }

            setLoadingMockupDetails(false);

            // Stop if the complete editable mockup could not be loaded
            if (!response?.success || !Array.isArray(response?.results)) {
                setMockupLoadError(response?.error_message || "Could not load mockup details.");
                return;
            }

            const loadedMockup = response.results.find((result) => Number(result?.id) === Number(mockup.id));

            if (!loadedMockup || !Array.isArray(loadedMockup.elements)) {
                setMockupLoadError("Could not load the saved mockup elements.");
                return;
            }

            const loadedElements = cloneElements(loadedMockup.elements);

            setMockupElements(loadedElements);
            setSavedMockupElements(cloneElements(loadedElements));

        };

        loadMockup();

        return () => { isCurrent = false; };

    }, [isOpen, mockup, fetchMockup]);


    // Build the editable print-area list while retaining each element's position in the complete saved element array
    const printAreaElements = useMemo(() => {

        const printAreas = [];

        // Keep the original element index so editing one card updates that exact saved element
        mockupElements.forEach((element, elementIndex) => {

            if (element?.type !== "print_area") { return; }

            printAreas.push({
                element: element,
                elementIndex: elementIndex,
            });

        });

        return printAreas;

    }, [mockupElements]);


    // Compare only the editable per-print-area compatibility settings
    const mockupHasUnsavedChanges =
        getPrintAreaViewSettingsSnapshot(mockupElements) !==
        getPrintAreaViewSettingsSnapshot(savedMockupElements);

    // Load valid sibling / child clone destinations for this custom product
    useEffect(() => {

        let isCurrent = true;

        // Reset the clone controls while the selected mockup changes
        setCloneTargets([]);
        setSelectedCloneTarget("");

        if (!isOpen || !mockup || !productCode || !getMockupCloneTargets) { return () => { isCurrent = false; }; }

        const loadCloneTargets = async () => {

            const response = await getMockupCloneTargets(productCode, mockup.id);
            if (!isCurrent || !response?.success) { return; }

            const targets = Array.isArray(response.results?.targets) ? response.results.targets : [];

            setCloneTargets(targets);
            setSelectedCloneTarget(targets.length > 0 ? targets[0].product_code : "");

        };

        loadCloneTargets();

        return () => { isCurrent = false; };

    }, [isOpen, mockup, productCode, getMockupCloneTargets]);



    // Clone to one selected family product
    const handleCloneToTarget = async () => {

        if (!selectedCloneTarget || cloning) { return; }

        setCloning(true);
        const response = await handleCloneMockup(mockup, selectedCloneTarget, "N");
        setCloning(false);

        if (response?.success) { onClose?.(); }

    };


    // Clone this mockup to every available family target
    const handleCloneToAll = async () => {

        if (cloneTargets.length === 0 || cloning) { return; }

        setCloning(true);

        let allSuccessful = true;

        for (const target of cloneTargets) {

            const response = await handleCloneMockup(mockup, target.product_code, "N");

            if (!response?.success) {
                allSuccessful = false;
                break;
            }

        }

        setCloning(false);

        if (allSuccessful) { onClose?.(); }

    };

    // Read the current view settings for one saved print-area element
    const getElementViewSettings = (element) => {

        const aspects = Array.isArray(element?.data?.view_settings?.aspects)
            ? element.data.view_settings.aspects.map((aspect) => String(aspect).trim().toUpperCase())
            : [];

        const rotations = Array.isArray(element?.data?.view_settings?.rotations)
            ? element.data.view_settings.rotations.map((rotation) => Number(rotation))
            : [];

        return {
            aspects: aspects,
            rotations: rotations,
        };

    };


    // Toggle one supported view setting for one physical print-area element
    const handleSettingToggle = (elementIndex, settingName, settingValue) => {

        setMockupElements((previousElements) => {

            // Update only the selected saved element
            return previousElements.map((element, currentElementIndex) => {

                if (currentElementIndex !== elementIndex) { return element; }

                const currentViewSettings = getElementViewSettings(element);
                const currentValues = currentViewSettings[settingName];
                const valueIsSelected = currentValues.includes(settingValue);

                // Every print-area compatibility group must keep at least one option selected
                if (valueIsSelected && currentValues.length === 1) { return element; }

                let updatedValues = [...currentValues];

                // Remove or add this exact compatibility value
                if (valueIsSelected) {
                    updatedValues = updatedValues.filter((currentValue) => currentValue !== settingValue);
                } else {
                    updatedValues.push(settingValue);
                }

                return {
                    ...element,
                    data: {
                        ...(element?.data || {}),
                        view_settings: {
                            ...currentViewSettings,
                            [settingName]: updatedValues,
                        },
                    },
                };

            });

        });

    };


    // Render one selectable setting button for one print-area element
    const renderSettingButton = (element, elementIndex, settingName, settingValue, label) => {

        const viewSettings = getElementViewSettings(element);
        const isSelected = viewSettings[settingName].includes(settingValue);

        let buttonClassName = "selectable__chip";
        if (isSelected) { buttonClassName += " selectable__chip--selected"; }

        return (
            <button
                type="button"
                className={buttonClassName}
                aria-pressed={isSelected}
                onClick={() => handleSettingToggle(elementIndex, settingName, settingValue)}
            >
                {label}
            </button>
        );

    };


    // Resolve a friendly print-area title and physical location subtitle
    const getPrintAreaDisplay = (element) => {

        let matchingSurface = null;
        let matchingPrintArea = null;

        // Resolve friendly names from the currently edited custom product when this location belongs to it
        if (element?.product_code === customProduct?.code && Array.isArray(customProduct?.surfaces)) {

            matchingSurface = customProduct.surfaces.find((surface) => surface?.code === element?.surface_code) || null;

            if (matchingSurface && Array.isArray(matchingSurface?.print_areas)) {
                matchingPrintArea = matchingSurface.print_areas.find((printArea) => printArea?.code === element?.print_area_code) || null;
            }

        }

        const title = matchingPrintArea?.name || element?.print_area_code || "Print Area";
        const surfaceName = matchingSurface?.name || element?.surface_code || "";
        const subtitleParts = [];

        if (element?.product_code) { subtitleParts.push(element.product_code); }
        if (surfaceName) { subtitleParts.push(surfaceName); }

        return {
            title: title,
            subtitle: subtitleParts.join(" · "),
        };

    };


    // Save the complete element set with only the selected print-area view settings changed
    const handleSave = async () => {

        if (!mockupHasUnsavedChanges || saving) { return; }

        setSaving(true);
        const response = await handleUpdateMockup(mockup, mockupElements);
        setSaving(false);

        // Keep the local comparison state accurate if the parent chooses not to close the popup
        if (response?.success) {
            setSavedMockupElements(cloneElements(mockupElements));
        }

    };


    // If isOpen is false return early (don't show the pop up)
    if (!isOpen) return null;


    return (

        <div className="popup-overlay">
        
            <div className="popup-container popup-container--small mockup-popup">

                {/* This is the modal header */}
                <div className="popup__header">

                    {/* This is the title area */}
                    <div className="popup__headerLeft">

                        {/* This is the parent title */}
                        <h2 className="popup__title">

                            Mockup

                        </h2>

                        {/* This is the parent subtitle */}
                        <p className="popup__subtitle">
                            {mockup?.id}
                        </p>

                    </div>

                    {/* This is the header actions */}
                    <div className="popup__headerRight">

                        {/* This is the close button */}
                        <button type="button" className="popup__close" onClick={() => onClose?.()} title="Close">
                            <CloseIcon />
                        </button>

                    </div>

                </div>


                <div className="popup-content popup-content__body mockup-popup__body">

                    <div className="view-popup_container mockup-popup__image-area">

                        <div className="view-popup_image-container mockup-popup__image-container">
                            
                            <ImageLoader src={mockup.mockup_image_url} alt="Mockup" className="surface-details_image" wrapperCSS="NA" imageLoaderImageCSS="view_bg-image-NA" />

                        </div>

                    </div>


                    <div className="mockup-popup__settings-panel">

                        <div className="mockup-popup__settings-heading">
                            <h3>Print Areas</h3>
                            <p>Set the compatible artwork aspects and rotations for each physical print area in this mockup.</p>
                        </div>

                        {loadingMockupDetails &&
                            <div className="mockup-popup__settings-message">Loading print areas...</div>
                        }

                        {!loadingMockupDetails && mockupLoadError &&
                            <div className="mockup-popup__settings-message mockup-popup__settings-message--error">{mockupLoadError}</div>
                        }

                        {!loadingMockupDetails && !mockupLoadError && printAreaElements.length === 0 &&
                            <div className="mockup-popup__settings-message">This mockup has no print-area elements.</div>
                        }

                        {!loadingMockupDetails && !mockupLoadError && printAreaElements.map(({ element, elementIndex }) => {

                            const display = getPrintAreaDisplay(element);

                            return (

                                <div key={`${element.product_code || ""}|${element.surface_code || ""}|${element.print_area_code || ""}|${elementIndex}`} className="mockup-popup__print-area-card">

                                    <div className="mockup-popup__print-area-heading">
                                        <strong>{display.title}</strong>
                                        {display.subtitle && <span>{display.subtitle}</span>}
                                    </div>

                                    <div className="mockup-popup__print-area-setting">

                                        <span className="mockup-popup__print-area-label">Aspects</span>

                                        <div className="selectable__chips">
                                            {renderSettingButton(element, elementIndex, "aspects", "S", "Square")}
                                            {renderSettingButton(element, elementIndex, "aspects", "T", "Tall")}
                                            {renderSettingButton(element, elementIndex, "aspects", "W", "Wide")}
                                        </div>

                                    </div>

                                    <div className="mockup-popup__print-area-setting">

                                        <span className="mockup-popup__print-area-label">Rotations</span>

                                        <div className="selectable__chips">
                                            {renderSettingButton(element, elementIndex, "rotations", 0, "0")}
                                            {renderSettingButton(element, elementIndex, "rotations", 90, "90")}
                                            {renderSettingButton(element, elementIndex, "rotations", 180, "180")}
                                            {renderSettingButton(element, elementIndex, "rotations", 270, "270")}
                                        </div>

                                    </div>

                                </div>

                            );

                        })}


                        {cloneTargets.length > 0 &&

                            <div className="mockup-popup__clone-section">

                                <TwoColumnList
                                    property={<label>Clone to</label>}
                                    value={
                                        <div className="mockup-clone-controls">
                                            <DropdownSelect
                                                name="mockup_clone_target"
                                                value={selectedCloneTarget}
                                                onChange={(event) => setSelectedCloneTarget(event.target.value)}
                                                options={cloneTargets.map((target) => ({
                                                    value: target.product_code,
                                                    name: (target.sku ? target.sku + " - " : "") + target.product_code
                                                }))}
                                                disabled={cloning}
                                            />

                                            <CreateButton
                                                title={cloning ? "Cloning..." : "Clone"}
                                                tooltip="Clone Mockup"
                                                size="s"
                                                onClick={handleCloneToTarget}
                                                disabled={cloning || !selectedCloneTarget}
                                            />

                                            <CreateButton
                                                title={cloning ? "Cloning..." : "Clone To All"}
                                                tooltip="Clone Mockup to All"
                                                size="s"
                                                onClick={handleCloneToAll}
                                                disabled={cloning}
                                            />
                                        </div>
                                    }
                                />

                            </div>

                        }

                    </div>

                </div>


                <div className="tabs-footer tabs-footer--fill-no-background mockup-popup__footer">

                    <div className="mockup-popup__footer-left">

                        <CreateButton title={deleteLoading ? "Deleting..." : "Delete"} isDelete={true} tooltip="Delete Mockup" size="m" icon="/images/icon_bin.png" disabled={deleteLoading} onClick={() => handleDeleteMockup(mockup)} />

                    </div>

                    <div className="mockup-popup__footer-right">

                        <CreateButton
                            title={isCustomizrReady ? "Edit in Customizr" : "Loading Customizr..."}
                            tooltip="Edit Mockup"
                            size="m"
                            onClick={() => {
                                onClose?.();
                                handleOpenCustomizrClick("mockup", mockup.id);
                            }}
                            disabled={!isCustomizrReady}
                        />

                        <CreateButton
                            title={saving ? "Saving..." : "Save"}
                            tooltip="Save Mockup"
                            size="m"
                            onClick={handleSave}
                            disabled={!mockupHasUnsavedChanges || loadingMockupDetails || saving}
                        />

                    </div>

                </div>

            </div>

            {saving &&
            
                <LoadingScreen loadingMessage="Saving..." />
            
            }

            {deleteLoading &&
            
                <LoadingScreen loadingMessage="Deleting..." />

            }
            
        </div>
        
    )

}; export default RenderMockupPopup;